import { v4 } from "uuid"
import { INSIGHTS_FIELD, INSIGHTS_SECTION } from "./linqset-base"

export const PROFILE_KEY = 'profile'    // where the signed-in user is kept in local storage

export const USER_NAME_MIN = 3
export const USER_NAME_MAX = 30

export const EMAIL_MIN = 3
export const EMAIL_MAX = 254    // the longest an email address can be

export const PASSWORD_MIN = 8
export const PASSWORD_MAX = 40

export enum ONBOARDING_STEPS {
    WALKTHROUGH,                // Visited walkthrough
    TUTORIAL,                   // Visited tutorial
    FIRST_MODEL,                // Created first model
    HOME_INTRO_SHOWN,           // Have shown the intro popup for home screen
    HELP_VIDEOS,                // Have opened the help video set
    FIRST_MODEL_SAVE,            // Saved a change to a model
    FIRST_INSIGHTS,           // First time gone to insights page
    CHECKLIST_HIDDEN          // Hid the getting started checklist. New steps go at the end: they are stored as numbers
}

export enum TICKET_PRIORITY {
    LOW=1,
    MEDIUM=2,
    HIGH=3,
    URGENT=4
}

export enum TICKET_TYPE {
    QUESTION = "Question",
    INCIDENT = "Incident",
    PROBLEM = "Problem",
    FEATURE = "Feature Request",
    LEAD = "Lead"
}

export enum NOTIFICATION_TYPE {
    ORGANISATION_EXPIRY,
    MODEL_ARCHIVE_EXPIRY,
    REQUEST_FOR_PROJECT_ACCESS,
    REQUEST_FOR_MODEL_ACCESS,
}

export enum USER_ROLE {
    ADMIN = 1,
    WRITE = 2,
    READ = 3,
    GUEST = 4
}

export enum LAYOUT_DIRECTION {
    UP = 'bottom-to-top',
    DOWN = 'top-to-bottom',
    LEFT_RIGHT = 'left-to-right',
    RIGHT_LEFT = 'right-to-left'
}

export enum NODE_MOVE_OPTION {
    SELECTED,               // Node must be selected first
    SHIFT_MOVE_NODE,      // hold shift and press on node to move
    SHIFT_DRAW_EDGE    // hold shidt to draw edge, just press to move
}

export enum EDGE_STYLE_ROUTING {
    OCTILINEAR = 'octilinear',
    ORTHOGONAL = 'orthogonal',
    POLYLINE = 'polyline',
    CURVED = 'curved'
}

export enum EDGE_GROUPING {
    SOURCE = 'source',
    TARGET = 'target',
    SOURCE_AND_TARGET = 'source-and-target',
    UNGROUP = 'ungroup'
}

// directions and edge routing styles were numbers before v2.0.0
const OLD_DIRECTIONS = [LAYOUT_DIRECTION.UP, LAYOUT_DIRECTION.DOWN, LAYOUT_DIRECTION.LEFT_RIGHT, LAYOUT_DIRECTION.RIGHT_LEFT]
const OLD_ROUTING_STYLES = [EDGE_STYLE_ROUTING.OCTILINEAR, EDGE_STYLE_ROUTING.ORTHOGONAL, EDGE_STYLE_ROUTING.POLYLINE, EDGE_STYLE_ROUTING.CURVED]

// brings a user's preferences up to date: old numbered settings (also when stored as text) become their names, and
// any setting missing takes its default
export const convertOldPreferences = (user:User) => {
    const defaults = createUserPreferences()
    if (!user.preferences) {
        user.preferences = defaults
        return
    }
    const preferences = user.preferences
    const fromNumber = <T>(value:unknown, values:T[]):T|unknown => {
        const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
        return typeof n === 'number' && values[n] !== undefined ? values[n] : value
    }
    preferences.graphDirection = fromNumber(preferences.graphDirection, OLD_DIRECTIONS) as LAYOUT_DIRECTION
    preferences.edgeRoutingStyle = fromNumber(preferences.edgeRoutingStyle, OLD_ROUTING_STYLES) as EDGE_STYLE_ROUTING

    ;(Object.keys(defaults) as (keyof Preferences)[]).forEach(k => {
        if (preferences[k] === undefined || preferences[k] === null) (preferences as any)[k] = defaults[k]
    })
}

export interface Notification {
    _key: string
    organisationKey:string
    organisationName:string
    type:NOTIFICATION_TYPE
    read:boolean
    userKey:string      // key of user that made request
    text:string         // system constructed text for this notification
    notes:string        // additional notes created by user
    referenceKey:string          // reference to some object relevant to this notification
    created:Date
    expiry:Date
}

export const getNotificationTypeName = (type:NOTIFICATION_TYPE) => {
    switch (type) {
        case NOTIFICATION_TYPE.MODEL_ARCHIVE_EXPIRY:
            return "Model archive expiry"
        case NOTIFICATION_TYPE.ORGANISATION_EXPIRY:
            return "Organisation subscription expiry"
        case NOTIFICATION_TYPE.REQUEST_FOR_MODEL_ACCESS:
            return "Requests for model access"
        case NOTIFICATION_TYPE.REQUEST_FOR_PROJECT_ACCESS:
            return "Requests for project access"

    }
}

// takes just the organisation's key and name, so this file needn't import the organisation model (which imports it)
export const createNotification = (organisation:{_key:string, name:string}, type:NOTIFICATION_TYPE, user:User, text:string, notes:string, referenceKey:string, days:number) => {
    const created = new Date()
    const expiry = new Date()
    expiry.setDate(created.getDate() + days)
    return {
        _key: v4(),
        organisationKey: organisation && organisation._key,
        organisationName: organisation && organisation.name,
        type,
        read:false,
        userKey:user && user._key,
        text,
        notes,
        referenceKey,
        created,
        expiry
    } as Notification
}

export interface Preferences {
    graphDirection:LAYOUT_DIRECTION,
    nodeModeOption: NODE_MOVE_OPTION,
    edgeRoutingStyle: EDGE_STYLE_ROUTING
    edgeGrouping: EDGE_GROUPING,
    fieldTypes: INSIGHTS_FIELD[]
    sections: INSIGHTS_SECTION[]
    peopleSystemsLayout?: string    // the layout (one of LAYOUTS) last chosen in the people and systems views, to open them in
}

// a change to the settings that affect how models are drawn or handled, for the canvases showing them to apply at once
export interface PreferencesChange {
    before: Preferences
    after: Preferences
}

// a copy of preferences brought up to date, so ones saved before v2.0.0 (numbers) compare with their names
export const normalisedPreferences = (preferences:Preferences|undefined) => {
    const holder = { preferences: preferences ? { ...preferences } : undefined } as User
    convertOldPreferences(holder)
    return holder.preferences
}

// the settings a canvas acts on: the direction it lays out in, how edges are routed, and how nodes are moved
export const changedDrawingPreferences = (before:Preferences|undefined, after:Preferences|undefined) => {
    if (!before || !after) return { direction: false, routing: false, move: false }
    const b = normalisedPreferences(before), a = normalisedPreferences(after)
    return {
        direction: b.graphDirection !== a.graphDirection,
        routing: b.edgeRoutingStyle !== a.edgeRoutingStyle,
        // older profiles may hold it as text
        move: Number(b.nodeModeOption) !== Number(a.nodeModeOption),
    }
}

export interface AccessRights {
    userKey: string
    access: USER_ROLE
    activated: boolean
    lastAccess?: Date       // when they last opened the organisation (LINQ administrators' visits aren't recorded)
}

// used to track where a new user may have come from
export interface UTMCampaign {
    id?:string,
    source?:string,
    medium?:string,
    name?:string,
    content?:string,
    term?:string,
    fbc?:string,        // facebook cookie
    fbp?:string     // facebook cookie
}

// the steps of getting started that the onboarding funnel counts, in order. The server records when a user who registered
// themselves first reaches each one; invited users aren't in the funnel
export enum FUNNEL_STEP {
    REGISTERED = "registered",
    ACTIVATED = "activated",
    ORGANISATION = "organisation",      // created an organisation
    DESCRIBED = "described",            // AI described one of their processes
    MODEL_BUILT = "modelBuilt",         // AI built a model
    FIRST_EDIT = "firstEdit",           // saved a change to a model themselves
    INSIGHTS = "insights",              // opened the insights page
    INVITED = "invited",                // invited someone to an organisation
}

export const FUNNEL_STEPS = Object.values(FUNNEL_STEP)

// when the user first reached each step
export type Funnel = {[step in FUNNEL_STEP]?: Date}

export interface  User {
    _key: string,
    currentOrganisationKey?: string,
    name: string,
    email: string,
    password?:string,
    createdDate: Date,
    activationCode?: string,
    codeCreatedDate: Date,
    signInDate?: Date,
    userAgent?: string,
    disabled:boolean,
    onboardingSteps:ONBOARDING_STEPS[],
    funnel?:Funnel,                       // only for users who registered themselves
    preferences:Preferences,
    notifications:Notification[]
    utm:UTMCampaign|undefined
    lockTime:Date|undefined,              // time user was locked. if undefined is not locked
    failureCount:number,                  // number of times failed password
    tokensValidAfter?:Date                // sessions that began before this (when the password was last set) are ended
    passwordReset?:boolean                // a reset was asked for: its link sets a new password, but the old one works until then
    signedOut?:{jti:string, expires:Date}[]   // sessions signed out of, until they would have expired anyway
    msOid?:string                         // the Microsoft account the user signs in with, once they have

    // system wide user role.
    role: USER_ROLE
}

export interface UserToken {
    _key: string,
    createdDate: Date,
    version: string,
    jti?: string            // the session's own id, so signing out can end it
}

export const createUser = (name:string, email:string, needsActivation:boolean, password?:string, utm?:UTMCampaign) => {
    return {
        _key: v4(),
        name,
        email,
        role: USER_ROLE.WRITE,
        password,
        createdDate: new Date(),
        activationCode: needsActivation ? v4() : undefined,
        codeCreatedDate: needsActivation ? new Date() : undefined,
        disabled: false,
        onboardingSteps: [],
        preferences: createUserPreferences(),
        notifications: [],
        utm,
        insightsFilters: [],
        lockTime:undefined,
        failureCount:0
    } as User
}

export const createUserPreferences = () => {
    return {
        graphDirection:LAYOUT_DIRECTION.DOWN,
        nodeModeOption: NODE_MOVE_OPTION.SELECTED,
        edgeRoutingStyle: EDGE_STYLE_ROUTING.POLYLINE,
        edgeGrouping: EDGE_GROUPING.UNGROUP,
        fieldTypes: [INSIGHTS_FIELD.ANNUAL_HOURS, INSIGHTS_FIELD.PROCESSED, INSIGHTS_FIELD.ACTIONS_SUPPORTED],
        sections: [INSIGHTS_SECTION.OVERVIEW, INSIGHTS_SECTION.MODELS, INSIGHTS_SECTION.ACTIONS, INSIGHTS_SECTION.INPUTS, INSIGHTS_SECTION.OUTPUTS, INSIGHTS_SECTION.ROLES, INSIGHTS_SECTION.SYSTEMS]
    } as Preferences
}

export const getRoleList = () => {
    return [getRoleObject(USER_ROLE.ADMIN), getRoleObject(USER_ROLE.WRITE), getRoleObject(USER_ROLE.READ)]
}

export const getUserRoleName = (role:USER_ROLE) => {
    if (!role) return "No access"
    switch (role) {
        case USER_ROLE.ADMIN: return "Admin"
        case USER_ROLE.WRITE: return "Write"
        case USER_ROLE.READ: return "Read only"
        case USER_ROLE.GUEST: return "Guest"
    }
}

const getRoleObject = (role:USER_ROLE) => {
    return {
        name: getUserRoleName(role),
        value: role
    }
}

export interface SupportTicket {
    id?: number,
    type: TICKET_TYPE,
    subject: string,
    description: string,
    status: TICKET_STATUS,
    priority: TICKET_PRIORITY,
    created_at: Date,
    due_by: Date,
    updated_at: Date
    context?: string        // where the ticket was raised (page, version, browser), sent with the description
}

export const TICKET_SUBJECT_MIN = 2
export const TICKET_SUBJECT_MAX = 100
export const TICKET_DESCRIPTION_MIN = 10
export const TICKET_DESCRIPTION_MAX = 2000
export const TICKET_CONTEXT_MAX = 1000

// the support desk's status numbers
export enum TICKET_STATUS {
    OPEN = 2,
    PENDING = 3,
    RESOLVED = 4,
    CLOSED = 5
}

export const getTicketStatusName = (status:TICKET_STATUS) => {
    switch (status) {
        case TICKET_STATUS.OPEN: return "Open"
        case TICKET_STATUS.PENDING: return "Waiting on you"
        case TICKET_STATUS.RESOLVED: return "Resolved"
        case TICKET_STATUS.CLOSED: return "Closed"
        default: return "In progress"
    }
}

export const isTicketDone = (status:TICKET_STATUS) => status === TICKET_STATUS.RESOLVED || status === TICKET_STATUS.CLOSED

export const getTicketPriorityName = (priority:TICKET_PRIORITY) => {
    switch (priority) {
        case TICKET_PRIORITY.LOW: return "Low"
        case TICKET_PRIORITY.MEDIUM: return "Medium"
        case TICKET_PRIORITY.HIGH: return "High"
        case TICKET_PRIORITY.URGENT: return "Urgent"
        default: return ""
    }
}

// a simple check that an email address has a name, an @ and a domain with a dot; the invite email confirms it is real
export const isValidEmail = (email:any) => typeof email === 'string' && email.length >= EMAIL_MIN && email.length <= EMAIL_MAX && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

// checks an invitation to join an organisation; returns the problem, if any
export const validateInvite = (invite:{name?:any, email?:any, role?:any}):string|undefined => {
    if (!invite) return "The invitation is missing"
    const name = typeof invite.name === 'string' ? invite.name.trim() : ''
    if (name.length < USER_NAME_MIN || name.length > USER_NAME_MAX) return `Name must be between ${USER_NAME_MIN} and ${USER_NAME_MAX} characters`
    if (!isValidEmail(typeof invite.email === 'string' ? invite.email.trim() : invite.email)) return "Enter a valid email address"
    if (!REQUESTABLE_ROLES.includes(invite.role)) return "Choose an organisation role"
    return undefined
}

// the access levels someone can ask an administrator for
export const REQUESTABLE_ROLES = [USER_ROLE.READ, USER_ROLE.WRITE, USER_ROLE.ADMIN]
export const ACCESS_REQUEST_NOTES_MAX = 500

// what each access level lets someone do, for choosing one to request
export const getRoleDescription = (role:USER_ROLE) => {
    switch (role) {
        case USER_ROLE.ADMIN: return "View, edit and manage who has access"
        case USER_ROLE.WRITE: return "View and edit"
        case USER_ROLE.READ: return "View only"
        default: return ""
    }
}

// checks a request for access; returns the problem, if any
export const validateAccessRequest = (request:{role?:any, notes?:any}):string|undefined => {
    if (!request) return "The request is missing"
    if (!REQUESTABLE_ROLES.includes(request.role)) return "Choose an access level"
    if (request.notes !== undefined && request.notes !== null && typeof request.notes !== 'string') return "The message must be text"
    if ((request.notes || '').length > ACCESS_REQUEST_NOTES_MAX) return `The message must be ${ACCESS_REQUEST_NOTES_MAX} characters or fewer`
    return undefined
}

// checks a new ticket before it is sent to the support desk; returns the problem, if any
export const validateSupportTicket = (ticket:Partial<SupportTicket>):string|undefined => {
    if (!ticket) return "Ticket details are missing"
    if (!Object.values(TICKET_TYPE).includes(ticket.type as TICKET_TYPE)) return "Choose a ticket type"
    if (!Object.values(TICKET_PRIORITY).includes(ticket.priority as TICKET_PRIORITY)) return "Choose a priority"
    if ((ticket.subject !== undefined && typeof ticket.subject !== 'string') || (ticket.description !== undefined && typeof ticket.description !== 'string')) return "The subject and description must be text"
    const subject = (ticket.subject || '').trim()
    if (subject.length < TICKET_SUBJECT_MIN || subject.length > TICKET_SUBJECT_MAX) return `Subject must be between ${TICKET_SUBJECT_MIN} and ${TICKET_SUBJECT_MAX} characters`
    const description = (ticket.description || '').trim()
    if (description.length < TICKET_DESCRIPTION_MIN || description.length > TICKET_DESCRIPTION_MAX) return `Description must be between ${TICKET_DESCRIPTION_MIN} and ${TICKET_DESCRIPTION_MAX} characters`
    if (ticket.context !== undefined && ticket.context !== null && (typeof ticket.context !== 'string' || ticket.context.length > TICKET_CONTEXT_MAX)) return "The page details are invalid"
    return undefined
}